import React, { useState } from 'react';
import Modal from '../ui/Modal';
import PdfIcon from '../icons/PdfIcon';
import PptIcon from '../icons/PptIcon';
import { API_URLS } from '../../config/api';
import { ExportQueryData } from '../../types/project';
import { MockDataService } from '../../services/mockData';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions, error?: string) => void;
  queryData?: ExportQueryData;
  projectName?: string;
}

interface ExportOptions {
  format: 'pdf' | 'powerpoint';
  pdfType?: 'strategy' | 'columbus';
  modules: string[];
  includeSourceMetadata: boolean;
  includeCharts: boolean;
  includeRecommendations: boolean;
  includeNextSteps: boolean;
  reportName?: string;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, onExport, queryData, projectName }) => {
  const [format, setFormat] = useState<'pdf' | 'powerpoint'>('pdf');
  const [pdfType] = useState<'strategy' | 'columbus'>('strategy');
  const [selectedModules, setSelectedModules] = useState<string[]>([
    'Overview', 'Market', 'Trends', 'Competitors', 'Customer_Insights', 'Technology'
  ]);
  const [includeSourceMetadata, setIncludeSourceMetadata] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeRecommendations, setIncludeRecommendations] = useState(true);
  const [includeNextSteps, setIncludeNextSteps] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Report name state with localStorage persistence
  const getStorageKey = (projectName: string) => `exportReportName_${projectName}`;

  const getLastReportName = (): string => {
    if (!projectName) return '';
    try {
      const stored = localStorage.getItem(getStorageKey(projectName));
      return stored || projectName;
    } catch {
      return projectName;
    }
  };

  const [reportName, setReportName] = useState(getLastReportName());

  // Save report name to localStorage when it changes
  const saveReportName = (name: string) => {
    if (!projectName) return;
    try {
      localStorage.setItem(getStorageKey(projectName), name);
    } catch {
      // Ignore localStorage errors
    }
  };

  // Handle report name changes
  const handleReportNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setReportName(newName);
    saveReportName(newName);
  };

  // Update report name when project name changes
  React.useEffect(() => {
    const lastReportName = getLastReportName();
    setReportName(lastReportName);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getLastReportName is recreated each render but only depends on projectName; effect must re-run only when projectName changes
  }, [projectName]);

  const modules = [
    { id: 'Overview', label: 'Overview' },
    { id: 'Market', label: 'Market' },
    { id: 'Trends', label: 'Trends' },
    { id: 'Competitors', label: 'Competitors' },
    { id: 'Customer_Insights', label: 'Customer Insights' },
    { id: 'Technology', label: 'Technology' }
  ];

  const handleModuleToggle = (moduleId: string) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  // Validation: Check if at least one module is selected
  const isExportDisabled = selectedModules.length === 0 || isExporting;

  const handleExport = async () => {
    // Prevent export if no modules are selected
    if (selectedModules.length === 0) {
      return;
    }

    setIsExporting(true);

    const options: ExportOptions = {
      format,
      pdfType: format === 'pdf' ? pdfType : undefined,
      modules: selectedModules,
      includeSourceMetadata,
      includeCharts,
      includeRecommendations,
      includeNextSteps
    };
    
    try {
      // Check for mock data scenarios (centralized)
      const queryText = queryData?.query || '';
      const scenario = MockDataService.findScenario(queryText);

      if (scenario) {
        try {
          const filePath = MockDataService.getExportFile(scenario.id, format);

          if (!filePath) {
            throw new Error('Export file not configured for this scenario');
          }

          console.log(`Downloading ${format} file for scenario: ${scenario.id}`);

          // Fetch the file
          const response = await fetch(filePath);
          if (!response.ok) {
            console.error(`File fetch failed. Status: ${response.status}, Path: ${filePath}`);
            throw new Error(`File not found: ${filePath}`);
          }

          // Get the blob from the response
          const blob = await response.blob();

          // Create object URL from blob
          const url = URL.createObjectURL(blob);

          // Extract filename from path
          const fileName = filePath.split('/').pop() || `export.${format === 'pdf' ? 'pdf' : 'pptx'}`;

          // Create a link to download the file
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the object URL
          URL.revokeObjectURL(url);

          // Notify parent component of successful export
          onExport(options);
        } catch (error) {
          console.error('Error downloading pre-existing file:', error);
          onExport(options, 'Failed to download the pre-existing file. Please try again.');
        } finally {
          setIsExporting(false);
          onClose();
        }
        return;
      }
      
      if (format === 'pdf' && queryData) {
        // Prepare the query_data with all required fields
        const preparedQueryData = {
          ...queryData.structured_response || {
            executive_summary: '',
            areas: {},
            recommendations: [],
            next_steps: [],
            agent_responses: {}
          },
          // Override areas with fallback logic
          areas: queryData.structured_response?.areas || queryData.areas || {}
        };
        
        // Call the appropriate PDF generation API based on selected type
        const pdfEndpoint = pdfType === 'columbus' 
          ? API_URLS.PDF_GENERATE_COLUMBUS
          : API_URLS.PDF_GENERATE_STRATEGY;
        
        const response = await fetch(pdfEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query_data: preparedQueryData,
            options: {
              sections: selectedModules.includes('Overview') ? ['executive_summary', ...selectedModules] : selectedModules,
              includeCharts,
              includeMetadata: includeSourceMetadata,
              includeRecommendations,
              includeNextSteps,
              projectName: reportName.trim() || projectName || 'Project Report',
              reportName: reportName.trim() || projectName || 'Project Report'
            }
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.pdf_base64) {
            // Download the PDF using the report name or project name as fallback
            const finalReportName = reportName.trim() || projectName || 'Project Report';
            const safeName = finalReportName.replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_');
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${result.pdf_base64}`;
            link.download = result.filename || `${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Notify parent component of successful export
            onExport(options);
          } else if (result.success === false) {
            // Handle specific error from PDF generator
            const errorMessage = result.error || 'Unknown error occurred during PDF generation';
            console.error('PDF generation failed:', errorMessage);
            onExport(options, errorMessage);
          } else {
            console.error('PDF generation failed: No PDF data received');
            onExport(options, 'Failed to generate PDF. No data received from server.');
          }
        } else {
          const errorText = await response.text();
          console.error('PDF generation failed:', errorText);

          // Try to parse error response for better error messages
          try {
            const errorData = JSON.parse(errorText);
            const errorMessage = errorData.detail || errorData.error || 'Server error occurred';
            onExport(options, `Failed to generate PDF: ${errorMessage}`);
          } catch {
            onExport(options, 'Failed to generate PDF. Server error occurred.');
          }
        }
      } else if (format === 'powerpoint' && queryData) {
        // Extract charts from result with areas fallback
        const extractChartsFromResult = (result: any) => {
          const charts: any[] = [];
          // Primary: Check direct areas property (new structure)
          // Fallback: Check structured_response.areas (legacy structure)
          const areas = result.structured_response?.areas || result.areas || {};
          if (areas && Object.keys(areas).length > 0) {
            Object.values(areas).forEach((area: any) => {
              if (area.charts) {
                charts.push(...area.charts);
              }
            });
          }
          return charts;
        };
        
        // Prepare query data for PPTX generation
        const preparedQueryData = {
          queryId: queryData.queryId || 'unknown',
          query: queryData.query || 'Strategic Analysis',
          status: 'completed',
          executive_summary: queryData.structured_response?.executive_summary || '',
          charts: extractChartsFromResult(queryData),
          areas: queryData.structured_response?.areas || queryData.areas || {}, // Add areas at root level with fallback
          swot_analysis: queryData.structured_response?.swot_analysis || queryData.swot_analysis || null, // Add SWOT at root level
          structured_response: {
            executive_summary: queryData.structured_response?.executive_summary || '',
            areas: queryData.structured_response?.areas || queryData.areas || {}, // Areas with fallback
            recommendations: queryData.structured_response?.recommendations || [],
            next_steps: queryData.structured_response?.next_steps || [],
            swot_analysis: queryData.structured_response?.swot_analysis || queryData.swot_analysis || null // Add SWOT analysis
          }
        };
        
        // Call PPTX generation API
        const response = await fetch(API_URLS.PPTX_GENERATE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query_data: preparedQueryData,
            options: {
              sections: selectedModules.includes('Overview') ? ['executive_summary', ...selectedModules] : selectedModules,
              include_charts: includeCharts,
              includeRecommendations,
              includeNextSteps,
              organizationName: 'Aspire Bank',
              replaceOrganizationNames: false,
              projectName: reportName.trim() || projectName || 'Project Report',
              reportName: reportName.trim() || projectName || 'Project Report'
            }
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.pptx_base64) {
            // Decode base64 and create blob
            const binaryString = atob(data.pptx_base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { 
              type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' 
            });
            
            // Download the PPTX file using the report name or project name as fallback
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const finalReportName = reportName.trim() || projectName || 'Project Report';
            const safeName = finalReportName.replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_');
            link.download = data.filename || `${safeName}_${new Date().toISOString().split('T')[0]}.pptx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            onExport(options);
          } else {
            console.error('PPTX generation failed: No data received');
            onExport(options, 'Failed to generate PowerPoint. No data received from server.');
          }
        } else {
          const errorText = await response.text();
          console.error('PPTX generation failed:', errorText);
          onExport(options, 'Failed to generate PowerPoint. Server error occurred.');
        }
      } else {
        // No query data available
        onExport(options, 'No data available to export. Please run a query first.');
      }
    } catch (error) {
      console.error('Export failed:', error);
      onExport(options, 'An error occurred while exporting. Please try again.');
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Project"
      maxWidth="640px"
      className="export-modal"
    >
      <div className="section">
        <h3 className="export-section-title">Report Name</h3>
        <div className="report-name-input">
          <input
            type="text"
            id="reportName"
            value={reportName}
            onChange={handleReportNameChange}
            placeholder={projectName || 'Enter report name...'}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              marginBottom: '8px'
            }}
          />
          <div style={{ fontSize: '12px', color: '#666' }}>
            This name will be used for the exported file. Your last used name is automatically saved and restored.
          </div>
        </div>
      </div>

      <div className="section">
        <h3 className="export-section-title">Report Format</h3>
        <div className="format-options">
          <div
            className={`format-option ${format === 'pdf' ? 'selected' : ''}`}
            onClick={() => setFormat('pdf')}
          >
            <div className="format-icon">
              <PdfIcon size={24} className="format-icon-svg" />
            </div>
            <div className="format-info">
              <div className="format-title">PDF Report</div>
              <div className="format-subtitle">Complete insights report</div>
            </div>
            <div className="format-radio">
              <input
                type="radio"
                name="format"
                checked={format === 'pdf'}
                onChange={() => setFormat('pdf')}
              />
            </div>
          </div>

          <div
            className={`format-option ${format === 'powerpoint' ? 'selected' : ''}`}
            onClick={() => setFormat('powerpoint')}
          >
            <div className="format-icon">
              <PptIcon size={24} className="format-icon-svg" />
            </div>
            <div className="format-info">
              <div className="format-title">PowerPoint</div>
              <div className="format-subtitle">Presentation slides</div>
            </div>
            <div className="format-radio">
              <input
                type="radio"
                name="format"
                checked={format === 'powerpoint'}
                onChange={() => setFormat('powerpoint')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="section">
        <h3 className="export-section-title">Select Modules</h3>
        <div className="modules-grid">
          {modules.map((module) => (
            <div key={module.id} className="module-option">
              <input
                type="checkbox"
                id={module.id}
                checked={selectedModules.includes(module.id)}
                onChange={() => handleModuleToggle(module.id)}
              />
              <label htmlFor={module.id}>{module.label}</label>
            </div>
          ))}
        </div>
        {selectedModules.length === 0 && (
          <div className="validation-message" style={{ color: '#e74c3c', fontSize: '14px', marginTop: '8px' }}>
            Please select at least one module to export.
          </div>
        )}
      </div>

      <div className="section">
        <h3 className="export-section-title">More Options</h3>
        <div className="options-grid">
          <div className="option-item">
            <input
              type="checkbox"
              id="metadata"
              checked={includeSourceMetadata}
              onChange={(e) => setIncludeSourceMetadata(e.target.checked)}
            />
            <label htmlFor="metadata">Include Source Metadata</label>
          </div>
          {format === 'pdf' && (
            <>
              <div className="option-item">
                <input
                  type="checkbox"
                  id="charts"
                  checked={includeCharts}
                  onChange={(e) => setIncludeCharts(e.target.checked)}
                />
                <label htmlFor="charts">Include Charts & Visualizations</label>
              </div>
              <div className="option-item">
                <input
                  type="checkbox"
                  id="recommendations"
                  checked={includeRecommendations}
                  onChange={(e) => setIncludeRecommendations(e.target.checked)}
                />
                <label htmlFor="recommendations">Include Recommendations</label>
              </div>
              <div className="option-item">
                <input
                  type="checkbox"
                  id="nextSteps"
                  checked={includeNextSteps}
                  onChange={(e) => setIncludeNextSteps(e.target.checked)}
                />
                <label htmlFor="nextSteps">Include Next Steps</label>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="export-modal-footer">
        <button
          className="export-button"
          onClick={handleExport}
          disabled={isExportDisabled}
          title={selectedModules.length === 0 ? 'Please select at least one module to export' : ''}
        >
          {isExporting ? 'Exporting...' : 'Export'}
        </button>
      </div>
    </Modal>
  );
};

export default ExportModal;